import { displayText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
// EditDeposition.js (New File: src/components/WebManager/scenes/Sessions/Deposition/EditDeposition.js)
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import {
  updateDeposition,
  getDeposition,
  getCsrfToken,
  getCase,
} from '../../../../services/api';
import {
  validateField,
  validateForm,
} from '../../../../Utils/SessionsValidationsSchema';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import ClientConfig from '../../../../../config/Clientconfig';

const EditDeposition = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);

  // Typography from theme
  const fontFamily =
    theme?.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme?.typography?.bodyFontSize || 14;
  const titleFontSize = theme?.typography?.titleFontSize || bodyFontSize + 4;
  const smallFontSize = Math.max(bodyFontSize - 2, 10);
  const xSmallFontSize = Math.max(bodyFontSize - 4, 8);

  const { username, caseId, sessionId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    witness: '',
    volume: '',
    introducing_party: '',
    date: '',
    court_reporting_firm: '',
    location: '',
    session_passcode: '',
    details: '',
    schedule_video: false,
    registrants: '',
    meeting_duration: '',
    meeting_time: '',
    meeting_timezone: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [caseData, setCaseData] = useState(null);

  useEffect(() => {
    const initializeCsrf = async () => {
      try {
        await getCsrfToken();
      } catch (error) {
        console.error('Error initializing CSRF token:', error);
        toast.error('Failed to initialize security token.', {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontSize: `${bodyFontSize}px`,
            fontFamily,
            fontWeight: 500,
          },
          autoClose: 3000,
        });
      }
    };
    initializeCsrf();

    const fetchData = async () => {
      if (!caseId || isNaN(caseId)) {
        toast.error(displayText('Invalid case ID provided'), {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontSize: `${bodyFontSize}px`,
            fontFamily,
            fontWeight: 500,
          },
          autoClose: 3000,
        });
        return;
      }

      setIsLoading(true);
      try {
        const caseResponse = await getCase(caseId);
        if (caseResponse.status !== 200) {
          throw new Error('Case not found');
        }
        setCaseData(caseResponse.data.data);

        const depositionResponse = await getDeposition(sessionId);
        if (depositionResponse.status !== 200) {
          throw new Error('Deposition not found');
        }
        const data = depositionResponse.data;
        setFormData({
          witness: data.witness || '',
          volume: data.volume || '',
          introducing_party: data.introducing_party || '',
          date: data.date || '',
          court_reporting_firm: data.court_reporting_firm || '',
          location: data.location || '',
          session_passcode: data.session_passcode || '',
          details: data.details || '',
          schedule_video: data.schedule_video || false,
          registrants: data.registrants || '',
          meeting_duration: data.meeting_duration || '',
          meeting_time: data.meeting_time || '',
          meeting_timezone: data.meeting_timezone || '',
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(`${displayText("Failed to fetch ")}${ClientConfig.sessions.depositionLabel}${displayText(" or case details.")}`, {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontSize: `${bodyFontSize}px`,
            fontFamily,
            fontWeight: 500,
          },
          autoClose: 3000,
        });
        navigate(`/${username}/dashboard/case/${caseId}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [caseId, sessionId, username, navigate, theme.colors, bodyFontSize, fontFamily]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target || {};
    const updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    };
    setFormData(updatedFormData);
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    await validateField(name, formData, setErrors, 'deposition');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    console.log('Starting form submission...');
    setIsSubmitting(true);
    setIsLoading(true);

    try {
      const isValid = await validateForm(formData, setErrors, 'deposition');
      if (!isValid) {
        toast.error('Please fill all required fields correctly.', {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontSize: `${bodyFontSize}px`,
            fontFamily,
            fontWeight: 500,
          },
          autoClose: 3000,
        });
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }

      if (!caseId || isNaN(caseId)) {
        toast.error(displayText('Invalid case ID provided'), {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontSize: `${bodyFontSize}px`,
            fontFamily,
            fontWeight: 500,
          },
          autoClose: 3000,
        });
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }

      const payload = {
        case_id: parseInt(caseId),
        witness: formData.witness,
        volume: formData.volume,
        introducing_party: formData.introducing_party,
        date: formData.date,
        court_reporting_firm: formData.court_reporting_firm,
        location: formData.location,
        session_passcode: formData.session_passcode,
        details: formData.details,
        schedule_video: formData.schedule_video,
        registrants: formData.schedule_video ? formData.registrants : null,
        meeting_duration: formData.schedule_video
          ? formData.meeting_duration
          : null,
        meeting_time: formData.schedule_video ? formData.meeting_time : null,
        meeting_timezone: formData.schedule_video
          ? formData.meeting_timezone
          : null,
      };

      console.log('Submitting payload:', payload);

      const response = await updateDeposition(sessionId, payload);

      if (response.status === 204) {
        toast.warn(
          `${ClientConfig.sessions.depositionLabel} may have been updated, but no content was returned.`,
          {
            toastStyle: {
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              color: theme.colors.warning,
              fontSize: `${bodyFontSize}px`,
              fontFamily,
              fontWeight: 500,
            },
            autoClose: 3000,
          }
        );
        setTimeout(() => {
          navigate(
            `/${username}/dashboard/case/${caseId}/session/${sessionId}`
          );
        }, 3000);
        return;
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`${displayText("")}${ClientConfig.sessions.depositionLabel}${displayText(" session updated successfully")}`, {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.success,
            fontSize: `${bodyFontSize}px`,
            fontFamily,
            fontWeight: 500,
          },
          autoClose: 3000,
        });
        setTimeout(() => {
          navigate(
            `/${username}/dashboard/case/${caseId}/session/${sessionId}`
          );
        }, 3000);
        return;
      }

      const errorData = response.data || {};
      console.error('Unexpected response status:', response.status, errorData);
      let errorMessage = errorData.message || `Failed to update ${ClientConfig.sessions.depositionLabel}`;

      if (errorData.errors) {
        const errorDetails = Object.values(errorData.errors).flat().join(' ');
        errorMessage = `${errorMessage}: ${errorDetails}`;
        setErrors(errorData.errors);
      }

      toast.error(errorMessage, {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontSize: `${bodyFontSize}px`,
          fontFamily,
          fontWeight: 500,
        },
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Submission error:', error.response?.data || error.message);

      let errorMessage =
        error.response?.data?.message ||
        error.message ||
        `An error occurred while updating the ${ClientConfig.sessions.depositionLabel} session`;

      if (error.response?.data?.errors) {
        const errorDetails = Object.values(
          error.response.data.errors
        ).flat().join(' ');
        errorMessage = `${errorMessage}: ${errorDetails}`;
        setErrors(error.response.data.errors);
      }

      toast.error(errorMessage, {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontSize: `${bodyFontSize}px`,
          fontFamily,
          fontWeight: 500,
        },
        autoClose: 5000,
      });
    } finally {
      console.log('Submission complete, resetting loading states');
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) {
      return;
    }
    navigate(`/${username}/dashboard/case/${caseId}/session/${sessionId}`);
  };

  return (
    <div
      style={{
        fontFamily,
        fontSize: `${bodyFontSize}px`,
        fontWeight: 500,
        minHeight: '100vh',
        backgroundColor: theme.colors.bgPrimary,
        color: theme.colors.textPrimary,
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          fontSize: `${bodyFontSize}px`,
          fontFamily,
          fontWeight: 500,
        }}
      />

      <nav
        aria-label="breadcrumb"
        style={{
          fontSize: `${xSmallFontSize}px`,
          margin: '5px',
          marginLeft: '25px',
        }}
      >
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link
              to={`/${username}/dashboard/cases`}
              style={{
                color: theme.colors.primary,
                textDecoration: 'underline',
              }}
            >
              {/*label from ClientConfig */}
              {ClientConfig.cases.managementLabel}
            </Link>
          </li>
          <li
            className="breadcrumb-item active"
            style={{ color: theme.colors.textPrimary }}
          >
            {/* label from ClientConfig */}
            {caseData?.case_name || 'Loading...'} / {ClientConfig.sessions.editDepositionLabel}
          </li>
        </ol>
      </nav>

      <div
        style={{
          padding: '16px',
          backgroundColor: theme.colors.bgPrimary,
          boxShadow:
            '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div
          style={{
            borderRadius: '10px',
            border: `1px solid ${theme.colors.borderColor}`,
            overflow: 'hidden',
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: '12px',
            }}
          >
            <h5
              style={{
                fontSize: `${smallFontSize}px`,
                fontWeight: 600,
                margin: 0,
              }}
            >
              {/*  label from ClientConfig */}
              {ClientConfig.sessions.editDepositionLabel}
            </h5>
            <h5
              style={{
                fontSize: `${smallFontSize}px`,
                fontWeight: 600,
                margin: 0,
              }}
            >
              {/*  label from ClientConfig */}
              Related To {ClientConfig.cases.label}: {caseData?.case_name || 'Loading...'}
            </h5>
          </div>
          <div
            style={{
              padding: '16px',
            }}
          >
            <form id="editDepositionForm" onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: window.innerWidth < 600 ? 'column' : 'row',
                  gap: '24px',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {/* Witness */}
                  <div>
                    <label
                      htmlFor="witness"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Witness*
                    </label>
                    <input
                      type="text"
                      id="witness"
                      name="witness"
                      value={formData.witness}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter witness name"
                      required
                      disabled={isSubmitting}
                      className={`form-control ${
                        errors.witness ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.witness
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 100,
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.witness && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.witness}
                      </div>
                    )}
                  </div>

                  {/* Volume */}
                  <div>
                    <label
                      htmlFor="volume"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Volume
                    </label>
                    <input
                      type="text"
                      id="volume"
                      name="volume"
                      value={formData.volume}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter volume (optional)"
                      disabled={isSubmitting}
                      className={`form-control ${
                        errors.volume ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.volume
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 100,
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.volume && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.volume}
                      </div>
                    )}
                  </div>

                  {/* Introducing Party */}
                  <div>
                    <label
                      htmlFor="introducingParty"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Introducing Party*
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: `1px solid ${
                          errors.introducing_party
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        transition: 'border-color 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <input
                        type="text"
                        id="introducingParty"
                        name="introducing_party"
                        value={formData.introducing_party}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Type only the first name to search users"
                        required
                        disabled={isSubmitting}
                        className={`form-control ${
                          errors.introducing_party ? 'is-invalid' : ''
                        }`}
                        style={{
                          flex: 1,
                          border: 'none',
                          borderRadius: '4px 0 0 4px',
                          padding: '6px 10px',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 100,
                          backgroundColor: theme.colors.bgPrimary,
                          color: '#666',
                          outline: 'none',
                          boxShadow: 'none',
                          opacity: isSubmitting ? 0.6 : 1,
                          fontFamily: 'inherit',
                        }}
                      />
                      <span
                        style={{
                          padding: '6px 8px',
                          backgroundColor: theme.colors.bgPrimary,
                          color: theme.colors.textPrimary,
                          borderRadius: '0 4px 4px 0',
                        }}
                      >
                        <i
                          className="bi bi-search"
                          style={{ fontSize: `${xSmallFontSize}px` }}
                        ></i>
                      </span>
                    </div>
                    {errors.introducing_party && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.introducing_party}
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <label
                      htmlFor="date"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Date*
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        border: `1px solid ${
                          errors.date
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        transition: 'border-color 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        disabled={isSubmitting}
                        className={`form-control ${
                          errors.date ? 'is-invalid' : ''
                        }`}
                        style={{
                          flex: 1,
                          border: 'none',
                          borderRadius: '4px 0 0 4px',
                          padding: '6px 10px',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 100,
                          backgroundColor: theme.colors.bgPrimary,
                          color: '#666',
                          outline: 'none',
                          boxShadow: 'none',
                          opacity: isSubmitting ? 0.6 : 1,
                          fontFamily: 'inherit',
                        }}
                      />
                      <span
                        style={{
                          padding: '6px 8px',
                          backgroundColor: theme.colors.bgPrimary,
                          color: theme.colors.textPrimary,
                          borderRadius: '0 4px 4px 0',
                        }}
                      >
                        <i
                          className="bi bi-calendar"
                          style={{ fontSize: `${xSmallFontSize}px` }}
                        ></i>
                      </span>
                    </div>
                    {errors.date && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.date}
                      </div>
                    )}
                  </div>

                  {/* Court Reporting Firm */}
                  <div>
                    <label
                      htmlFor="courtReportingFirm"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Court Reporting Firm Name*
                    </label>
                    <select
                      id="courtReportingFirm"
                      name="court_reporting_firm"
                      value={formData.court_reporting_firm}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${
                        errors.court_reporting_firm ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.court_reporting_firm
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 100,
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      <option value="">Select Firm</option>
                      <option value="Veritext Legal Solutions">
                        Veritext Legal Solutions
                      </option>
                      <option value="Esquire Deposition Solutions">{displayText(" Esquire Deposition Solutions ")}</option>
                      <option value="U.S. Legal Support">
                        U.S. Legal Support
                      </option>
                      <option value="Lexitas">Lexitas</option>
                      <option value="Magna Legal Services">
                        Magna Legal Services
                      </option>
                      <option value="PohlmanUSA Court Reporting">
                        PohlmanUSA Court Reporting
                      </option>
                      <option value="Planet Depos">Planet Depos</option>
                    </select>
                    {errors.court_reporting_firm && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.court_reporting_firm}
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label
                      htmlFor="location"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Location* (City of {ClientConfig.sessions.depositionLabel})
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${
                        errors.location ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.location
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 100,
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.location && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.location}
                      </div>
                    )}
                  </div>

                  {/* Session Passcode */}
                  <div>
                    <label
                      htmlFor="sessionPasscode"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      {ClientConfig.sessions.label} Passcode*
                    </label>
                    <input
                      type="text"
                      id="sessionPasscode"
                      name="session_passcode"
                      value={formData.session_passcode}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="34586"
                      required
                      disabled={isSubmitting}
                      className={`form-control ${
                        errors.session_passcode ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.session_passcode
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 100,
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.session_passcode && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.session_passcode}
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div>
                    <label
                      htmlFor="details"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Details
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      value={formData.details}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter additional details (optional)"
                      rows="2"
                      disabled={isSubmitting}
                      className={`form-control ${
                        errors.details ? 'is-invalid' : ''
                      }`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.details
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 100,
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        resize: 'vertical',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.details && (
                      <div
                        className="invalid-feedback"
                        style={{
                          color: theme.colors.danger,
                          display: 'block',
                          fontSize: `${xSmallFontSize}px`,
                          fontWeight: 500,
                          marginTop: '2px',
                        }}
                      >
                        {errors.details}
                      </div>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div
                  style={{
                    width: window.innerWidth < 600 ? '100%' : '1px',
                    height: window.innerWidth < 600 ? '1px' : 'auto',
                    backgroundColor: 'transparent',
                    backgroundImage:
                      window.innerWidth < 600
                        ? `repeating-linear-gradient(to right, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`
                        : `repeating-linear-gradient(to bottom, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`,
                    margin:
                      window.innerWidth < 600 ? '12px 0' : '0 16px',
                    minHeight:
                      window.innerWidth >= 600 ? '400px' : '1px',
                  }}
                />

                {/* Right Column */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {/* Toggle schedule video */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <label
                      htmlFor="scheduleVideo"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: `${xSmallFontSize}px`,
                        fontWeight: 600,
                        margin: 0,
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                    >
                      Schedule a DepoDash Conference?
                    </label>
                    <div
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: formData.schedule_video
                          ? theme.colors.success
                          : '#ccc',
                        cursor: isSubmitting
                          ? 'not-allowed'
                          : 'pointer',
                        transition: 'background-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                      onClick={() => {
                        if (!isSubmitting) {
                          setFormData((prev) => ({
                            ...prev,
                            schedule_video: !prev.schedule_video,
                          }));
                        }
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: formData.schedule_video ? '28px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: theme.colors.textWhite,
                          boxShadow:
                            '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'left 0.3s ease',
                        }}
                      />
                      <input
                        type="checkbox"
                        id="scheduleVideo"
                        name="schedule_video"
                        checked={formData.schedule_video}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={{
                          display: 'none',
                        }}
                      />
                    </div>
                  </div>

                  {formData.schedule_video && (
                    <>
                      {/* Registrants */}
                      <div>
                        <label
                          htmlFor="registrants"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 600,
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Registrants
                        </label>
                        <input
                          type="text"
                          id="registrants"
                          name="registrants"
                          value={formData.registrants}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="Enter registrants"
                          disabled={isSubmitting}
                          className={`form-control ${
                            errors.registrants ? 'is-invalid' : ''
                          }`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.registrants
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 100,
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow:
                              '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        />
                        {errors.registrants && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: `${xSmallFontSize}px`,
                              fontWeight: 500,
                              marginTop: '2px',
                            }}
                          >
                            {errors.registrants}
                          </div>
                        )}
                      </div>

                      {/* Meeting Duration */}
                      <div>
                        <label
                          htmlFor="meetingDuration"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 600,
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Meeting Duration*
                        </label>
                        <input
                          type="text"
                          id="meetingDuration"
                          name="meeting_duration"
                          value={formData.meeting_duration}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="e.g., 2 hours"
                          required
                          disabled={isSubmitting}
                          className={`form-control ${
                            errors.meeting_duration ? 'is-invalid' : ''
                          }`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.meeting_duration
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 100,
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow:
                              '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        />
                        {errors.meeting_duration && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: `${xSmallFontSize}px`,
                              fontWeight: 500,
                              marginTop: '2px',
                            }}
                          >
                            {errors.meeting_duration}
                          </div>
                        )}
                      </div>

                      {/* Meeting Time */}
                      <div>
                        <label
                          htmlFor="meetingTime"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 600,
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Meeting Time*
                        </label>
                        <input
                          type="time"
                          id="meetingTime"
                          name="meeting_time"
                          value={formData.meeting_time}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          disabled={isSubmitting}
                          className={`form-control ${
                            errors.meeting_time ? 'is-invalid' : ''
                          }`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.meeting_time
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 100,
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow:
                              '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        />
                        {errors.meeting_time && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: `${xSmallFontSize}px`,
                              fontWeight: 500,
                              marginTop: '2px',
                            }}
                          >
                            {errors.meeting_time}
                          </div>
                        )}
                      </div>

                      {/* Meeting Timezone */}
                      <div>
                        <label
                          htmlFor="meetingTimezone"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 600,
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Meeting Timezone*
                        </label>
                        <select
                          id="meetingTimezone"
                          name="meeting_timezone"
                          value={formData.meeting_timezone}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          disabled={isSubmitting}
                          className={`form-control ${
                            errors.meeting_timezone ? 'is-invalid' : ''
                          }`}
                          style={{
                            width: '100%',
                            border: `1px solid ${
                              errors.meeting_timezone
                                ? theme.colors.danger
                                : theme.colors.borderColor
                            }`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: `${xSmallFontSize}px`,
                            fontWeight: 100,
                            backgroundColor: theme.colors.bgPrimary,
                            color: '#666',
                            outline: 'none',
                            boxShadow:
                              '0 2px 4px rgba(0, 0, 0, 0.05)',
                            transition: 'border-color 0.3s ease',
                            opacity: isSubmitting ? 0.6 : 1,
                            fontFamily: 'inherit',
                          }}
                        >
                          <option value="">Select Timezone</option>
                          <option value="America/New_York">
                            Eastern Time (US and Canada)
                          </option>
                          <option value="America/Chicago">
                            Central Time (US and Canada)
                          </option>
                          <option value="America/Denver">
                            Mountain Time (US and Canada)
                          </option>
                          <option value="America/Los_Angeles">
                            Pacific Time (US and Canada)
                          </option>
                          <option value="UTC">UTC</option>
                          <option value="Asia/Kolkata">
                            India Standard Time
                          </option>
                        </select>
                        {errors.meeting_timezone && (
                          <div
                            className="invalid-feedback"
                            style={{
                              color: theme.colors.danger,
                              display: 'block',
                              fontSize: `${xSmallFontSize}px`,
                              fontWeight: 500,
                              marginTop: '2px',
                            }}
                          >
                            {errors.meeting_timezone}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer buttons */}
      <div
        style={{
          display: 'flex',
          flexDirection: window.innerWidth < 600 ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
          padding: '12px',
          borderBottom: `1px solid ${theme.colors.borderColor}`,
          backgroundColor: theme.colors.bgPrimary,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            width: window.innerWidth < 600 ? '100%' : 'auto',
            position: 'relative',
          }}
        >
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            className="action-button"
            style={{
              background: theme.gradients.secondary,
              color: theme.colors.textWhite,
              fontSize: `${xSmallFontSize}px`,
              fontWeight: 600,
              padding: '8px 12px',
              borderRadius: '8px',
              border: 'none',
              transition: 'opacity 0.3s ease',
              boxShadow: 'none',
              width: window.innerWidth < 600 ? '100%' : 'auto',
              minWidth: '120px',
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontFamily,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="editDepositionForm"
            disabled={isLoading}
            className="action-button"
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              fontSize: `${xSmallFontSize}px`,
              fontWeight: 600,
              padding: '8px 12px',
              borderRadius: '8px',
              border: 'none',
              transition: 'opacity 0.3s ease',
              boxShadow: 'none',
              width: window.innerWidth < 600 ? '100%' : 'auto',
              minWidth: '120px',
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily,
            }}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>

          {isLoading && (
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: `4px solid ${theme.colors.primary}`,
                  borderTop: '4px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          )}

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    </div>
  );
};

export default EditDeposition;